import React from 'react';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Tooltip, Typography } from '@mui/material';

import { Order } from '@luxuryescapes/contract-svc-order';

import OrderItemTotalBusinessCredit from '~/components/Purchases/OrderDetail/BusinessTraveller/OrderItemTotalBusinessCredit';
import { formatSupplierCost } from '~/components/Purchases/utils/formatSupplierCost';

import { LUX_PLUS } from '~/consts/membership';

import { formatCurrency } from '~/utils/currencyFormatter';

interface Props {
  room: Order.BedbankItemRoom;
  currencyCode: string;
  order: Order.Order;
  orderItemId: string;
}

export default function BedbankPricingSummary({ room, currencyCode, order, orderItemId }: Props) {
  const orderItem = order.bedbank_items.find((item) => item.id === orderItemId);
  const isPropertyCollect = orderItem?.payment_method === 'propertyCollect';
  const lePaySupplierAmount = isPropertyCollect ? 0 : room.supplier_cost_inclusive ?? room.supplier_price_inclusive;
  const supplierPayLeAmount = room.supplier_cost_inclusive == null ? room.supplier_marketing_fee : null;
  const lePaySupplierDisplay = formatSupplierCost(lePaySupplierAmount, room.supplier_currency_code);
  const supplierPayLeDisplay = formatSupplierCost(supplierPayLeAmount, room.supplier_currency_code);
  const hasDifferentSupplierCurrency = !!room.supplier_currency_code && room.supplier_currency_code !== currencyCode;
  const displayCost = isPropertyCollect ? 0 : room.cost_price;
  const displayOriginalCost = isPropertyCollect ? 0 : room.rebooked?.parent_cost_price ?? 0;
  const displayRebookedCost = isPropertyCollect ? 0 : room.rebooked?.cost_price ?? 0;
  const supplierPayLeLabel = (
    <>
      Supplier Pay LE{' '}
      <Tooltip title="Marketing fee is paid after the stay">
        <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.secondary', verticalAlign: 'middle', cursor: 'help' }} />
      </Tooltip>
      :
    </>
  );

  return (
    <>
      <Typography variant="subtitle1" fontWeight="bold">
        Pricing
      </Typography>

      {!room.rebooked && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography>
            LE Pay Supplier:{' '}
            <Typography component="span" color={hasDifferentSupplierCurrency ? 'error' : 'inherit'}>
              {lePaySupplierDisplay}
            </Typography>
          </Typography>
          {supplierPayLeAmount != null && (
            <Typography>
              {supplierPayLeLabel}{' '}
              <Typography component="span" color={hasDifferentSupplierCurrency ? 'error' : 'inherit'}>
                {supplierPayLeDisplay}
              </Typography>
            </Typography>
          )}
          <Typography>
            Cost: {formatCurrency(displayCost, currencyCode, 'en-AU', { maximumFractionDigits: 2 })}
          </Typography>
          <Typography>
            Current Margin:{' '}
            {formatCurrency(room.price - room.cost_price, currencyCode, 'en-AU', { maximumFractionDigits: 2 })} (
            {(((room.price - room.cost_price) / room.price) * 100).toFixed(2)}%)
          </Typography>
          {order.business_id !== null && (
            <OrderItemTotalBusinessCredit orderItemId={orderItemId} currencyCode={currencyCode} />
          )}
          <Typography>
            Sell Price: {formatCurrency(room.price, currencyCode, 'en-AU', { maximumFractionDigits: 2 })}
          </Typography>
          {!!room.lux_plus_price && (
            <Typography>
              {LUX_PLUS.PROGRAM_NAME} Price:{' '}
              {formatCurrency(room.lux_plus_price, currencyCode, 'en-AU', { maximumFractionDigits: 2 })}
            </Typography>
          )}
          <Typography>
            Public Price:{' '}
            {formatCurrency(room.public_price || room.price, currencyCode, 'en-AU', { maximumFractionDigits: 2 })}
          </Typography>
        </Box>
      )}

      {room.rebooked && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography pt={1} fontWeight={500}>
              Original
            </Typography>
            <Typography>
              Cost: {formatCurrency(displayOriginalCost, currencyCode, 'en-AU', { maximumFractionDigits: 2 })}
            </Typography>
            <Typography>
              Margin:{' '}
              {formatCurrency(room.price - room.rebooked.parent_cost_price, currencyCode, 'en-AU', {
                maximumFractionDigits: 2,
              })}{' '}
              ({(((room.price - room.rebooked.parent_cost_price) / room.price) * 100).toFixed(2)}%)
            </Typography>
            <Typography>
              Sell Price: {formatCurrency(room.price, currencyCode, 'en-AU', { maximumFractionDigits: 2 })}
              {room.price === room.lux_plus_price ? ' (LP)' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography pt={1} fontWeight={500}>
              Rebooked
            </Typography>
            <Typography>
              LE Pay Supplier:{' '}
              <Typography component="span" color={hasDifferentSupplierCurrency ? 'error' : 'inherit'}>
                {lePaySupplierDisplay}
              </Typography>
            </Typography>
            {supplierPayLeAmount != null && (
              <Typography>
                {supplierPayLeLabel}{' '}
                <Typography component="span" color={hasDifferentSupplierCurrency ? 'error' : 'inherit'}>
                  {supplierPayLeDisplay}
                </Typography>
              </Typography>
            )}
            <Typography>
              Cost: {formatCurrency(displayRebookedCost, currencyCode, 'en-AU', { maximumFractionDigits: 2 })}
            </Typography>
            <Typography>
              Margin:{' '}
              {formatCurrency(room.price - room.rebooked.cost_price, currencyCode, 'en-AU', {
                maximumFractionDigits: 2,
              })}{' '}
              ({(((room.price - room.rebooked.cost_price) / room.price) * 100).toFixed(2)}%)
            </Typography>
            <Typography>
              Incremental Margin:{' '}
              {formatCurrency(room.rebooked.parent_cost_price - room.rebooked.cost_price, currencyCode, 'en-AU', {
                maximumFractionDigits: 2,
              })}{' '}
              ({(((room.rebooked.parent_cost_price - room.rebooked.cost_price) / room.price) * 100).toFixed(2)}%)
            </Typography>
          </Box>
        </>
      )}
    </>
  );
}
